import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import authService from '../../services/authService';
import './Auth.css';

// Regex pattern for special characters (defined outside component to avoid JSX parsing issues)
const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/;

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    website: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Handle Google OAuth callback
    const googleToken = searchParams.get('google_token');
    const success = searchParams.get('success');
    const error = searchParams.get('error');

    if (googleToken && success === 'true') {
      // Store the token and navigate to dashboard
      localStorage.setItem('authToken', googleToken);
      (async () => {
        try {
          const t = localStorage.getItem('authToken');
          const res = await fetch('/api/userpreferences', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
          if (res.ok) {
            const prefs = await res.json();
            const dest = (prefs?.DefaultGlobalLanding || '/dashboard');
            navigate(dest);
            return;
          }
        } catch {}
        navigate('/dashboard');
      })();
    } else if (error) {
      switch (error) {
        case 'google_auth_failed':
          setError('Google authentication failed. Please try again.');
          break;
        case 'no_email_from_google':
          setError('No email address received from Google. Please try again.');
          break;
        case 'google_registration_failed':
          setError('Failed to create account with Google. Please try manual registration.');
          break;
        case 'google_auth_error':
          setError('An error occurred during Google authentication. Please try again.');
          break;
        default:
          setError('Authentication failed. Please try again.');
      }
    }
  }, [searchParams, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    // Check password match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    // Check password requirements (base app requirements)
    const password = formData.password;
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    // Special characters: !@#$%^&*()_+-=[]{}|;:,.<>?
    if (!SPECIAL_CHARS_REGEX.test(password)) {
      errors.push('Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)');
    }
    
    if (errors.length > 0) {
      setError(errors.join('. '));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      await authService.register(formData);
      setSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      
      // Handle different types of errors
      let errorMessage = 'Registration failed. Please try again.';
      
      if (err.message) {
        if (err.message.includes('Password')) {
          errorMessage = err.message;
        } else if (err.message.includes('validation') || err.message.includes('required')) {
          errorMessage = err.message;
        } else if (err.message.includes('email')) {
          errorMessage = err.message;
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    window.location.href = '/api/auth/google-login';
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Check Your Email!</h1>
            <p>We've sent a confirmation link to your email address.</p>
          </div>
          
          <div className="success-message">
            <p>
              Please check your email and click the confirmation link to activate your account.
              You'll need to confirm your email before you can sign in.
            </p>
          </div>

          <div className="auth-footer">
            <p>
              Already have an account?{' '}
              <Link to="/login" className="auth-link">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Create Account</h1>
                      <p>Join PocketPE and start creating amazing prompts!</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="Enter your first name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="Enter your last name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter your email"
            />
          </div>

          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={handleChange}
            autoComplete="off"
            tabIndex="-1"
            className="honeypot"
            aria-hidden="true"
          />

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Create a password"
            />
            <div className="password-requirements">
              <p>Password must contain:</p>
              <ul>
                <li className={formData.password.length >= 8 ? 'requirement-met' : 'requirement-unmet'}>
                  At least 8 characters
                </li>
                <li className={/[A-Z]/.test(formData.password) ? 'requirement-met' : 'requirement-unmet'}>
                  One uppercase letter (A-Z)
                </li>
                <li className={/[a-z]/.test(formData.password) ? 'requirement-met' : 'requirement-unmet'}>
                  One lowercase letter (a-z)
                </li>
                <li className={/[0-9]/.test(formData.password) ? 'requirement-met' : 'requirement-unmet'}>
                  One number (0-9)
                </li>
                <li className={SPECIAL_CHARS_REGEX.test(formData.password) ? 'requirement-met' : 'requirement-unmet'}>
                  One special character (!@#$%^&*()_+-=[]{}|;:,.&lt;&gt;?)
                </li>
              </ul>
              <p style={{ marginTop: '12px', fontSize: '0.875rem', color: '#f59e0b', fontStyle: 'italic' }}>
                ⚠️ Teams and Organizations may have more restrictive password requirements.
              </p>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Confirm your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="auth-button"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="google-sign-in-button"
            disabled={loading}
          >
            <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="auth-link">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register; 